/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, cloneElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Add, DragIndicator } from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ItemsSelectModal } from "components";
import {
  saveMenuSliderItems,
  reorderMenuSlider,
  updateProductActive,
} from "actions";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { FILE_TYPE } from "constants/fileTypes";
import "./MenuSlider.scss";

import { BrandButton } from "components";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const toSliderProducts = (products) => products.map((p) => ({ id: p.id }));

const SortableItem = ({ id, children }) => {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="sortable-item"
      {...attributes}
    >
      {cloneElement(children, { dragHandleProps: listeners })}
    </div>
  );
};

const MenuSliderCard = ({
  product,
  onRemove,
  onToggleActive,
  isActiveLoading,
  dragHandleProps,
}) => {
  const getImageUrl = (image) => `${imageBase}${image.path}`;

  return (
    <Card className={`menu-slider-card${!product.active ? " inactive" : ""}`}>
      <CardContent className="card-content">
        <Box className="card-header">
          <Box className="drag-handle" {...dragHandleProps}>
            <DragIndicator color="primary" fontSize="small" />
          </Box>
          <Chip
            label={`${ADMIN_TEXTS.common.order}: ${product.menuSliderOrder}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <Box className="card-body">
          <Box className="card-image">
            {product.image ? (
              product.image.type === FILE_TYPE.VIDEO ? (
                <video src={getImageUrl(product.image)} controls />
              ) : (
                <img src={getImageUrl(product.image)} alt={product.title} />
              )
            ) : null}
          </Box>

          <Box className="card-text">
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              className="card-title"
            >
              {product.title}
            </Typography>

            <Box className="card-footer">
              <FormControlLabel
                control={
                  <Switch
                    checked={product.active}
                    onChange={() => onToggleActive(product.id, !product.active)}
                    disabled={isActiveLoading}
                    size="small"
                  />
                }
                label={ADMIN_TEXTS.common.active}
                className="toggle-active"
              />
              <BrandButton
                variant="error"
                size="small"
                className="remove-button"
                onClick={() => onRemove(product)}
              >
                {ADMIN_TEXTS.common.remove}
              </BrandButton>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const MenuSlider = () => {
  const dispatch = useDispatch();
  const products = useSelector((state) => state.products.allProducts);
  const loading = useSelector((state) => state.products.loading);
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [activeLoadingId, setActiveLoadingId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  );

  useEffect(() => {
    const sliderItems = [...products]
      .filter((p) => p.isMenuSliderItem)
      .sort((a, b) => a.menuSliderOrder - b.menuSliderOrder);
    setItems(sliderItems);
  }, [products]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    const previous = items;
    const reordered = arrayMove(items, oldIndex, newIndex).map((item, i) => ({
      ...item,
      menuSliderOrder: i + 1,
    }));
    setItems(reordered);

    setReorderLoading(true);
    dispatch(reorderMenuSlider({ id: active.id, newOrder: newIndex + 1 }))
      .unwrap()
      .catch(() => setItems(previous))
      .finally(() => setReorderLoading(false));
  };

  const handleSave = async (selectedProducts) => {
    setSaving(true);
    try {
      await dispatch(
        saveMenuSliderItems({ products: toSliderProducts(selectedProducts) }),
      ).unwrap();
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id, active) => {
    setActiveLoadingId(id);
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, active } : p)));
    try {
      await dispatch(updateProductActive({ id, active })).unwrap();
    } catch {
      setItems((prev) =>
        prev.map((p) => (p.id === id ? { ...p, active: !active } : p)),
      );
    } finally {
      setActiveLoadingId(null);
    }
  };

  const handleRemove = async (product) => {
    await dispatch(
      saveMenuSliderItems({
        products: toSliderProducts(items.filter((p) => p.id !== product.id)),
      }),
    ).unwrap();
  };

  return (
    <Box className="menu-slider-page">
      <Box className="menu-slider-header">
        <Typography variant="h4" component="h1" fontWeight="bold">
          {ADMIN_TEXTS.menuSlider.pageTitle}
        </Typography>
        <BrandButton
          variant="primary"
          startIcon={<Add />}
          onClick={() => setModalOpen(true)}
        >
          {ADMIN_TEXTS.menuSlider.addToSlider}
        </BrandButton>
      </Box>

      {loading ? (
        <Box className="menu-slider-loading">
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper className="empty-state">
          <Typography variant="h6" color="text.secondary">
            {ADMIN_TEXTS.menuSlider.noItems}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {ADMIN_TEXTS.menuSlider.noItemsHint}
          </Typography>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={rectSortingStrategy}
          >
            <Box className="sortable-container">
              <Grid container spacing={2}>
                {items.map((product) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={product.id}
                    className="menu-slider-grid-item"
                  >
                    <SortableItem id={product.id}>
                      <MenuSliderCard
                        product={product}
                        onRemove={handleRemove}
                        onToggleActive={handleToggleActive}
                        isActiveLoading={activeLoadingId === product.id}
                      />
                    </SortableItem>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </SortableContext>
        </DndContext>
      )}

      {reorderLoading && (
        <Box className="reorder-indicator">
          <Alert severity="info" className="reorder-alert">
            <CircularProgress size={16} className="reorder-progress" />
            {ADMIN_TEXTS.menuSlider.reordering}
          </Alert>
        </Box>
      )}

      <ItemsSelectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        saving={saving}
        statusField="isMenuSliderItem"
        title={ADMIN_TEXTS.menuSlider.addToSlider}
      />
    </Box>
  );
};
